import React, { useState } from 'react';
import { FileText, Shield, AlertCircle } from 'lucide-react';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: `By accessing or using Olewin Academy's services, website, mobile applications, or enrolling in any course, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.

These terms constitute a legally binding agreement between you (the "Student" or "User") and Olewin Academy (the "Academy", "we", "us", or "our"). Your continued use of our services constitutes acceptance of any modifications to these terms.`
    },
    {
      id: 'services',
      title: '2. Services Provided',
      content: `Olewin Academy provides educational services including but not limited to:
      
• Online and offline coaching for competitive examinations (IIT-JEE, NEET, GATE, IIT-JAM, etc.)
• School education support (Classes 6-12)
• Personal coaching and mentorship programs
• Group training and batch classes
• Professional certification courses
• Study materials, practice tests, and mock examinations
• Career counseling and guidance
• Study abroad consultation

We reserve the right to modify, suspend, or discontinue any service at any time without prior notice. We are not liable for any modification, suspension, or discontinuation of services.`
    },
    {
      id: 'enrollment',
      title: '3. Enrollment and Registration',
      content: `3.1 Eligibility: You must be at least 13 years of age to enroll. Students under 18 require parental/guardian consent.

3.2 Accurate Information: You agree to provide accurate, current, and complete information during registration. False information may result in immediate termination without refund.

3.3 Account Security: You are responsible for maintaining the confidentiality of your login credentials. Any activity under your account is your responsibility.

3.4 Registration Fee: A non-refundable registration fee is charged at enrollment covering administrative costs, account setup, and initial study materials.

3.5 Enrollment Confirmation: Enrollment is confirmed only upon successful payment of fees. We reserve the right to reject any enrollment application.`
    },
    {
      id: 'fees',
      title: '4. Fees and Payment Terms',
      content: `4.1 Course Fees: All fees are clearly displayed on our website and are subject to change. Current students are not affected by fee increases during their enrollment period.

4.2 Payment Methods: We accept credit/debit cards, net banking, UPI, and bank transfers. EMI options are available through partner financial institutions.

4.3 Payment Schedule: Full payment is due at enrollment unless an installment plan is agreed upon. Failure to pay installments on time may result in course access suspension.

4.4 Late Payment: Late payment attracts a penalty of 2% per month. Access to live classes and study materials will be suspended until payment is received.

4.5 Taxes: All fees are exclusive of applicable taxes. Students are responsible for any taxes, duties, or government charges.

4.6 Currency: All fees are in Indian Rupees (INR) unless otherwise specified.`
    },
    {
      id: 'refund',
      title: '5. Refund and Cancellation Policy',
      content: `5.1 Registration Fee: The registration/enrollment fee is NON-REFUNDABLE under all circumstances.

5.2 Course Fee Refund: 
• Within 7 days of enrollment: 80% refund of course fee (20% deduction for administrative costs)
• 8-14 days after enrollment: 50% refund of course fee
• After 14 days: NO REFUND will be provided

5.3 No Refund Circumstances:
• Poor academic performance
• Failure to attend classes
• Change of mind or personal reasons
• Medical or family emergencies (course freeze available)
• Course not meeting expectations
• Exam postponement or cancellation by examination authorities

5.4 Refund Process: Refund requests must be submitted in writing. Approved refunds are processed within 15-30 business days to the original payment method.

5.5 Transferred/Switched Courses: No refund if you switch to another course. Price difference will be adjusted.

5.6 Service Disruption: If we cancel a course or cannot provide services due to our fault, full refund will be provided.

5.7 No Partial Refunds: We do not provide pro-rata refunds for unused course duration.`
    },
    {
      id: 'attendance',
      title: '6. Attendance and Academic Requirements',
      content: `6.1 Minimum Attendance: Students must maintain 75% attendance to continue in the program and receive completion certificate.

6.2 Consequences of Low Attendance: Below 75% attendance may result in:
• Ineligibility for completion certificate
• Course termination without refund
• Denial of doubt-clearing support
• Removal from batch WhatsApp groups

6.3 Leave Policy: Medical or emergency leave up to 30 days per year with valid documentation. Course duration will be extended accordingly.

6.4 Class Recordings: Recorded lectures are provided as backup only. Regular attendance to live classes is mandatory.

6.5 Assessment Participation: Participation in mock tests and assignments is mandatory for progress evaluation.`
    },
    {
      id: 'conduct',
      title: '7. Student Conduct and Code of Ethics',
      content: `7.1 Prohibited Behavior:
• Sharing account credentials with others
• Recording or distributing classes without permission
• Harassment or abusive behavior towards faculty or students
• Cheating in tests or plagiarism
• Sharing study materials with non-enrolled students
• Hacking or unauthorized access to our systems
• Using services for illegal purposes

7.2 Consequences: Violation may result in immediate termination without refund, legal action, and being blacklisted from future enrollment.

7.3 Respectful Communication: Students must maintain professional and respectful communication with faculty and staff.

7.4 Academic Integrity: All assignments and tests must be completed honestly. Cheating results in immediate course termination.`
    },
    {
      id: 'intellectual',
      title: '8. Intellectual Property Rights',
      content: `8.1 Ownership: All content including study materials, videos, notes, test papers, software, and trademarks are owned by Olewin Academy or licensed to us.

8.2 Limited License: Students receive a non-exclusive, non-transferable, limited license to access materials for personal educational use only.

8.3 Restrictions: You may NOT:
• Copy, distribute, or sell our materials
• Modify or create derivative works
• Reverse engineer our software
• Remove copyright notices
• Share materials on public platforms or file-sharing sites
• Record live classes without explicit written permission

8.4 Copyright Infringement: Unauthorized use constitutes copyright infringement and will result in legal action, including claims for damages.

8.5 Student Work: Work you submit (assignments, projects) grants us license to use for educational purposes.`
    },
    {
      id: 'disclaimer',
      title: '9. Disclaimers and Limitation of Liability',
      content: `9.1 No Guarantee of Results: We do NOT guarantee admission to any institution, exam success, rank achievement, or job placement. Success depends on individual effort, aptitude, and external factors beyond our control.

9.2 Course Content: While we strive for accuracy, we make no warranties regarding completeness, reliability, or suitability of course content. Exam patterns may change.

9.3 Third-Party Content: We are not responsible for third-party content, links, or services referenced in our materials.

9.4 Technical Issues: We are not liable for service interruptions, technical failures, internet connectivity issues, or data loss.

9.5 Limitation of Liability: To the maximum extent permitted by law, our total liability shall not exceed the fees paid by you for the specific course.

9.6 No Consequential Damages: We are not liable for any indirect, incidental, special, or consequential damages including loss of profit, data, or opportunity.

9.7 Force Majeure: We are not liable for failure to perform due to circumstances beyond our control (natural disasters, government actions, pandemics, etc.).`
    },
    {
      id: 'termination',
      title: '10. Termination',
      content: `10.1 Termination by Academy: We reserve the right to terminate your enrollment immediately for:
• Violation of these terms
• Non-payment of fees
• Misconduct or disruptive behavior
• Fraudulent activities
• Low attendance without valid reason

10.2 No Refund on Termination: Termination for cause results in forfeiture of all fees with no refund.

10.3 Termination by Student: You may terminate by providing 30 days written notice. Refund policy in Section 5 applies.

10.4 Effect of Termination: Upon termination, your access to all services, materials, and student portal is immediately revoked.`
    },
    {
      id: 'privacy',
      title: '11. Privacy and Data Protection',
      content: `11.1 Data Collection: We collect personal information including name, contact details, academic records, payment information, and usage data.

11.2 Data Usage: Your data is used for providing services, communication, improvement of services, and marketing (with opt-out option).

11.3 Data Sharing: We may share data with service providers, payment processors, and as required by law. We do not sell personal information.

11.4 Data Security: We implement reasonable security measures but cannot guarantee absolute security.

11.5 Detailed Privacy Policy: Please refer to our Privacy Policy for comprehensive information.`
    },
    {
      id: 'modifications',
      title: '12. Modifications to Terms',
      content: `We reserve the right to modify these Terms of Service at any time. Changes will be posted on our website with the "Last Updated" date. Continued use after modifications constitutes acceptance.

Material changes will be notified via email. It is your responsibility to regularly review these terms.`
    },
    {
      id: 'governing',
      title: '13. Governing Law and Jurisdiction',
      content: `These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Hyderabad, Telangana, India.

You agree to first attempt resolution through good faith negotiations before initiating legal proceedings.`
    },
    {
      id: 'misc',
      title: '14. Miscellaneous',
      content: `14.1 Entire Agreement: These terms constitute the entire agreement between you and Olewin Academy.

14.2 Severability: If any provision is found invalid, the remaining provisions remain in effect.

14.3 No Waiver: Our failure to enforce any right does not constitute a waiver of that right.

14.4 Assignment: You may not assign your rights under these terms. We may assign our rights without restriction.

14.5 Communication: Official communications will be sent to your registered email address.

14.6 Language: In case of translation, the English version prevails.`
    },
    {
      id: 'contact',
      title: '15. Contact Information',
      content: `For questions regarding these Terms of Service:

Olewin Academy
Email: olewinacademy@gmail.com
Phone: +91 8639449423
Address: [Official Address:- Olewin Group] India & USA 
Business Hours: Monday - Saturday, 10:00 AM - 6:00 PM IST
Last Updated: October 21, 2025`
    }
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '80px 20px 60px',
        textAlign: 'center',
        marginTop:'15px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <FileText size={60} color="#58A95B" style={{ marginBottom: '20px' }} />
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}>
            Terms of Service
          </h1>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, lineHeight: '1.6' }}>
            Please read these terms carefully before using Olewin Academy's services
          </p>
          <div style={{
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            border: '2px solid #ffc107',
            borderRadius: '10px',
            padding: '15px',
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'center'
          }}>
            <AlertCircle size={24} color="#ffc107" />
            <span style={{ fontSize: '0.95rem' }}>Last Updated: October 21, 2025</span>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section style={{ padding: '40px 20px', backgroundColor: '#fff3cd', borderBottom: '2px solid #ffc107' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <Shield size={30} color="#856404" style={{ flexShrink: 0, marginTop: '5px' }} />
            <div>
              <h3 style={{ color: '#856404', marginBottom: '10px', fontSize: '1.2rem' }}>Important Notice</h3>
              <p style={{ color: '#856404', lineHeight: '1.6', margin: 0 }}>
                These Terms of Service contain important information about your rights and obligations. 
                Please read them carefully. By enrolling in any course or using our services, you acknowledge 
                that you have read, understood, and agree to be bound by these terms. Special attention should 
                be paid to our NO REFUND policy for Registration Fee (Registration/Enrollment fee may vary from Course Selection)after enrollment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section style={{ padding: '60px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Table of Contents */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#000' }}>Table of Contents</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  style={{
                    color: '#58A95B',
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.paddingLeft = '12px';
                  }}
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          {sections.map((section, index) => (
            <div
              key={section.id}
              id={section.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                marginBottom: '20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                scrollMarginTop: '20px'
              }}
            >
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '20px',
                paddingBottom: '15px',
                borderBottom: '3px solid #58A95B'
              }}>
                {section.title}
              </h2>
              <div style={{
                color: '#333',
                lineHeight: '1.8',
                fontSize: '1rem',
                whiteSpace: 'pre-line'
              }}>
                {section.content}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Acknowledgment Section */}
      <section style={{
        padding: '60px 20px',
        background: '#000000',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', color:'wheat', fontWeight: 'bold', marginBottom: '1rem' }}>
            Acknowledgment
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', lineHeight: '1.6', opacity: 0.9 }}>
            By clicking "I Agree" during enrollment or by using our services, you acknowledge that you have read, 
            understood, and agree to be bound by these Terms of Service. If you do not agree, please do not use our services.
          </p>
          <div style={{
            backgroundColor: '#1a1a1a',
            padding: '20px',
            borderRadius: '10px',
            border: '2px solid #58A95B',
            marginTop: '30px'
          }}>
            <p style={{ fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>
              <strong>For questions or concerns regarding these terms:</strong><br />
              Email: olewinacademy@gmail.com | Phone: [+91 8639449423]
            </p>
          </div>
        </div>
      </section>

      <style>{`
        * {
          box-sizing: border-box;
        }

        @media (max-width: 768px) {
          section {
            padding: 40px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;